/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";

import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import { CloudFiltersResponse } from "../../models/http/dashboard/cloud/cloud-filters-model";
import { KPI } from "../../models/http/dashboard/kpi.model";
import { ReportParams } from "../../models/http/dashboard/report-params.model";
import { CloudScopeMode } from "../../models/ui/cloud-scope-mode.model";
import { CloudScopeOrderBy } from "../../models/ui/cloud-scope-orderby.model";

@Injectable({
	providedIn: "root"
})
export class DashboardCloudHttpService {
	private _dashboardCloudOperations = ENDPOINT_GROUPS.cloud.operations;
	constructor(private _communicationService: CommunicationService) {}

	public getCloudFilters(tenantId: string): Observable<CloudFiltersResponse> {
		const params = new HttpParams({ fromObject: { tenantId } });

		return this._communicationService.callService(
			this._dashboardCloudOperations.getCloudFilters.service,
			HttpMethods.GET,
			{
				params
			}
		);
	}

	public getCloudPerformance(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<ChartResponse<LineChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				cloudProviders,
				applications,
				locations,
				startDate,
				endDate
			}
		});

		return this._communicationService
			.callService(
				this._dashboardCloudOperations.getCloudPerformance.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map((response: LineChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	public getCloudEmissionsForPieChart(
		tenantId: string,
		startDate: string,
		endDate: string,
		cloudProviders: string[],
		locations: string[],
		applications: string[]
	): Observable<ChartResponse<PieChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				startDate,
				endDate,
				cloudProviders,
				locations,
				applications
			}
		});
		return this._communicationService
			.callService(
				this._dashboardCloudOperations.getTotalEmissionForPieChart.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	public getTotalCo2(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<KPI> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				cloudProviders,
				applications,
				locations,
				startDate,
				endDate
			}
		});
		return this._communicationService.callService(
			this._dashboardCloudOperations.getTotalCo2.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getWorst10Applications(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				cloudProviders,
				applications,
				locations,
				startDate,
				endDate
			}
		});
		return this._communicationService
			.callService(
				this._dashboardCloudOperations.getWorst10Applications.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getWorst10Locations(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				cloudProviders,
				applications,
				locations,
				startDate,
				endDate
			}
		});
		return this._communicationService
			.callService(
				this._dashboardCloudOperations.getWorst10Locations.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getEmissionsAnalysis(
		tenantId: string,
		startDate: string,
		endDate: string,
		locations: string[],
		applications: string[],
		mode: CloudScopeMode,
		orderBy: CloudScopeOrderBy
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				startDate,
				endDate,
				locations,
				applications,
				mode,
				orderBy
			}
		});
		return this._communicationService
			.callService(
				this._dashboardCloudOperations.getEmissionsAnalysis.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getRegionsEmissions(
		tenantId: string,
		startDate: string,
		endDate: string,
		cloudProviders: string[],
		locations: string[],
		applications: string[],
		mode: CloudScopeMode,
		orderBy: CloudScopeOrderBy
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				startDate,
				endDate,
				cloudProviders,
				locations,
				applications,
				mode,
				orderBy
			}
		});
		return this._communicationService
			.callService(
				this._dashboardCloudOperations.getRegionsEmissions.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public exportResponseInCsv(
		startDate: string,
		endDate: string,
		cloudProviders: string[],
		locations: string[],
		applications: string[],
		tenantId: string
	): Observable<Blob> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				endDate,
				cloudProviders,
				locations,
				applications,
				tenantId
			}
		});

		return this._communicationService.callService(
			this._dashboardCloudOperations.exportCsv.service,
			HttpMethods.GET,
			{ params, responseType: "blob" }
		);
	}

	public exportReport(
		reportParams: ReportParams,
		tenantId: string,
		format: "pdf" | "xls" = "pdf"
	): Observable<string> {
		const params = new HttpParams({
			fromObject: { tenantId }
		});

		const pathParams = { format };

		return this._communicationService.callService(
			this._dashboardCloudOperations.exportReport.service,
			HttpMethods.GET,
			{
				params,
				pathParams,
				responseType: "text"
			},
			reportParams
		);
	}
}
